import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Moment } from 'moment';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { MdNightsStay, MdPeople } from 'react-icons/md';
import { RootState } from '../../../../../../store/store';
import {
  Event,
  EventTicket,
} from '../../../../../SportsAndEventsPage/sportsAndEventsInterface';
import { MapWrapper } from '../../../../../../components/Map';
import { isMapsEnabled } from '../../../../../../utils/featureFlags';
import { parseEventDate } from '../../../../utils/eventPackageDates';
import EventsDpStepper, {
  EventsDpStep,
  EventsDpStepperBackButton,
} from '../EventsDpStepper';
import {
  EVENT_TYPE_FALLBACK_IMAGE,
  getEventTypeIcon,
  getEventTypeImage,
} from './eventTypeVisuals';
import './selectedEventBanner.less';

interface PaxCounts {
  adult?: number | string;
  child?: number | string;
  infant?: number | string;
}

interface SelectedEventBannerProps {
  event: Event;
  /** Selected ticket — drives the ticket-category chip. */
  ticket?: EventTicket | null;
  /** Package (hotel+flight) range. Nights are only shown when both are set. */
  packageFrom?: Moment | null;
  packageTo?: Moment | null;
  pax?: PaxCounts;
  /**
   * Step the flow is on. Leave unset for consumers that are not inside the
   * events-DP stepper flow (or previews of it): the banner then renders as a
   * plain identity hero, without the stepper strip or the back button.
   */
  currentStep?: EventsDpStep;
  onStepClick?: (step: EventsDpStep) => void;
  /** Extra root class, for views that restyle the slots below. */
  className?: string;
  /** Rendered at the end of the head row, before the stepper back button. */
  aside?: React.ReactNode;
  /** Rendered under the meta row, in the head's main column. */
  extra?: React.ReactNode;
}

const SelectedEventBanner: React.FC<SelectedEventBannerProps> = ({
  event,
  ticket,
  packageFrom,
  packageTo,
  pax,
  currentStep,
  onStepClick,
  className,
  aside,
  extra,
}) => {
  const { searchDatePicker: t, sportsAndEvents: sportsAndEvents_t } =
    useSelector((data: RootState) => data.config.translations);

  const [mapModalOpen, setMapModalOpen] = useState(false);

  // Track only which url failed, not the url to show. Deriving the src means
  // picking a different event (the component is reused, not remounted) recovers
  // to that event type's own image instead of keeping the previous fallback.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const intendedSrc = getEventTypeImage(event?.eventType?.id);
  const backgroundSrc =
    failedSrc === intendedSrc ? EVENT_TYPE_FALLBACK_IMAGE : intendedSrc;

  const eventDate = parseEventDate(event?.dateOfEvent);
  // The API sends 00:00 when the kick-off time is not published yet
  const eventTime =
    event?.timeOfEvent && event.timeOfEvent !== '00:00'
      ? event.timeOfEvent
      : null;

  // The country lives on the event itself, but some payloads only carry it
  // nested under the city — fall back to that before dropping it.
  const countryName = event?.country?.name || event?.city?.country?.name;
  const locationText = [event?.venue?.name, event?.city?.name, countryName]
    .filter(Boolean)
    .join(', ');

  // Same venue-map affordance EventTitleAndReview provides, preserved here
  // because the banner takes over that component's role in this view.
  // maps off (REACT_APP_MAPS_ENABLED) - the venue is plain text, not a map link
  const venueCoords =
    isMapsEnabled() && event?.venue?.latitude && event?.venue?.longitude
      ? { lat: event.venue.latitude, lng: event.venue.longitude }
      : null;

  const nights = useMemo(() => {
    if (!packageFrom || !packageTo) return null;
    const diff = packageTo.diff(packageFrom, 'days');
    return diff > 0 ? diff : null;
  }, [packageFrom, packageTo]);

  const paxText = useMemo(() => {
    if (!pax) return null;
    const parts: string[] = [];
    const adults = Number(pax.adult) || 0;
    const children = Number(pax.child) || 0;
    const infants = Number(pax.infant) || 0;

    if (adults > 0) {
      parts.push(`${adults} ${adults === 1 ? t.adult : t.adults}`);
    }
    if (children > 0) {
      parts.push(`${children} ${children === 1 ? t.child : t.children}`);
    }
    if (infants > 0) {
      parts.push(`${infants} ${infants === 1 ? t.infant : t.infants}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }, [pax, t]);

  const showStepper = currentStep !== undefined;

  const ticketCategory =
    ticket?.splittedCategoryName?.main || ticket?.categoryName || null;
  // EventTitleAndReview renders a tournament logo when the API supplies one;
  // the field is not on the Tournament interface, hence the cast.
  const tournamentLogo = (event?.tournament as { logo?: string })?.logo;

  const metaItems: React.ReactNode[] = [];

  if (eventDate || eventTime) {
    metaItems.push(
      <span className="event-hero-meta-item" key="date">
        <CalendarOutlined />
        {eventDate && <span>{eventDate.format('ddd, DD MMM YYYY')}</span>}
        {eventTime && (
          <>
            <span className="event-hero-meta-dot">·</span>
            <ClockCircleOutlined />
            <span>{eventTime}</span>
          </>
        )}
      </span>
    );
  }

  if (locationText) {
    metaItems.push(
      <span
        className={`event-hero-meta-item${venueCoords ? ' clickable-venue' : ''}`}
        key="location"
        onClick={venueCoords ? () => setMapModalOpen(true) : undefined}
      >
        <EnvironmentOutlined />
        <span>{locationText}</span>
      </span>
    );
  }

  if (nights) {
    metaItems.push(
      <span className="event-hero-meta-item" key="nights">
        <MdNightsStay />
        <span>
          {nights} {t.nights}
        </span>
        <span className="event-hero-meta-dot">·</span>
        <span>
          {packageFrom?.format('DD MMM')} – {packageTo?.format('DD MMM')}
        </span>
      </span>
    );
  }

  if (paxText) {
    metaItems.push(
      <span className="event-hero-meta-item" key="pax">
        <MdPeople />
        <span>{paxText}</span>
      </span>
    );
  }

  return (
    <div
      className={`selected-event-hero${showStepper ? '' : ' no-stepper'}${
        className ? ` ${className}` : ''
      }`}
    >
      <div className="event-hero-background">
        <img
          src={backgroundSrc}
          alt=""
          aria-hidden="true"
          onError={() => setFailedSrc(intendedSrc)}
        />
        <div className="event-hero-scrim" />
      </div>

      {/* No `content-wrapper` here: SearchResults already renders this subtree
          inside one, and content-wrapper is viewport-relative (90vw), so
          nesting it would push the content past the banner's own edges. */}
      <div className="event-hero-content">
        <div className="event-hero-head">
          <div className="event-hero-head-main">
            <div className="event-hero-chips">
              {event?.eventType?.name && (
                <span className="event-hero-chip event-hero-chip-type">
                  {event.eventType.name}
                </span>
              )}
              {event?.tournament?.name && (
                <span className="event-hero-chip event-hero-chip-tournament">
                  {tournamentLogo && (
                    <img
                      className="event-hero-chip-logo"
                      src={tournamentLogo}
                      alt={event.tournament.name}
                      width={16}
                      height={16}
                    />
                  )}
                  {event.tournament.name}
                </span>
              )}
              {ticketCategory && (
                <span className="event-hero-chip event-hero-chip-category">
                  {ticketCategory}
                </span>
              )}
            </div>

            <div className="event-hero-title-row">
              <span className="event-hero-type-icon">
                {getEventTypeIcon(event?.eventType)}
              </span>
              <h2 className="event-hero-title">
                {event?.name || sportsAndEvents_t.noTitle}
              </h2>
            </div>

            {!!metaItems.length && (
              <div className="event-hero-meta">
                {metaItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <span className="event-hero-meta-comma">,</span>
                    )}
                    {item}
                  </React.Fragment>
                ))}
              </div>
            )}

            {extra}
          </div>

          {aside}

          {showStepper && (
            <EventsDpStepperBackButton
              current={currentStep}
              onStepClick={onStepClick}
              className="event-hero-back-btn"
            />
          )}
        </div>

        {showStepper && (
          <div className="event-hero-stepper">
            <EventsDpStepper
              current={currentStep}
              onStepClick={onStepClick}
              hideBackButton
              fitToParent
            />
          </div>
        )}
      </div>

      {venueCoords && (
        <MapWrapper
          data={[
            {
              position: [venueCoords.lat, venueCoords.lng],
              price: 0,
              currency: '',
            },
          ]}
          title={event.venue?.name || sportsAndEvents_t?.venue}
          initialZoom={16}
          previewInitZoom={14}
          preview_type="small"
          isOpen={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SelectedEventBanner;
